import { cn } from "@/lib/utils";

import {
  FILE_TYPE_GLYPHS,
  type FileTypeGlyphKey,
} from "./file-type-icons.generated";
import { sourceIcon, type BrandIcon } from "./source-icons";

/*
 * The app's file-type icons: one mark per kind of file, the same at every
 * size (a list row's 16px, a card's tile, a grid's tile, a link chip). Word,
 * Excel, PowerPoint and Google files show their product's official mark, the
 * set link references use; every other format shows a white glyph on its
 * colour: red for PDF, green for tables, blue for prose, orange for decks,
 * violet for media, amber for archives, slate for the rest. Glyphs are
 * Phosphor's, vendored by scripts/generate-file-type-icons.mjs; folders keep
 * their line glyph.
 */

export type FileTypeKind =
  | "pdf"
  | "csv"
  | "spreadsheet"
  | "document"
  | "presentation"
  | "text"
  | "markdown"
  | "code"
  | "sql"
  | "html"
  | "image"
  | "vector"
  | "video"
  | "audio"
  | "archive"
  | "google-sheets"
  | "google-docs"
  | "google-slides"
  | "google-drive"
  | "generic";

export type ResourceMarkKey =
  | "pdf"
  | "document"
  | "sheet"
  | "slides"
  | "image"
  | "video"
  | "audio"
  | "archive"
  | "code"
  | "data"
  | "link"
  | "mail"
  | "file";

export const FILE_TYPES: Record<
  FileTypeKind,
  { label: string; mark: ResourceMarkKey; product?: string }
> = {
  pdf: { label: "PDF document", mark: "pdf" },
  csv: { label: "CSV spreadsheet", mark: "sheet" },
  spreadsheet: { label: "Excel workbook", mark: "sheet" },
  document: { label: "Word document", mark: "document" },
  presentation: { label: "Presentation", mark: "slides" },
  text: { label: "Plain text", mark: "document" },
  markdown: { label: "Markdown", mark: "document" },
  code: { label: "Code", mark: "code" },
  sql: { label: "SQL", mark: "data" },
  html: { label: "HTML", mark: "code" },
  image: { label: "Image", mark: "image" },
  vector: { label: "SVG image", mark: "image" },
  video: { label: "Video", mark: "video" },
  audio: { label: "Audio", mark: "audio" },
  archive: { label: "Archive", mark: "archive" },
  "google-sheets": {
    label: "Google Sheets",
    mark: "sheet",
    product: "google_sheets",
  },
  "google-docs": { label: "Google Docs", mark: "document", product: "google_docs" },
  "google-slides": {
    label: "Google Slides",
    mark: "slides",
    product: "google_slides",
  },
  "google-drive": {
    label: "Google Drive file",
    mark: "file",
    product: "google_drive",
  },
  generic: { label: "File", mark: "file" },
};

export const EXTENSION_KINDS: Record<string, FileTypeKind> = {
  pdf: "pdf",
  csv: "csv",
  tsv: "csv",
  xls: "spreadsheet",
  xlsx: "spreadsheet",
  xlsm: "spreadsheet",
  xltx: "spreadsheet",
  ods: "spreadsheet",
  numbers: "spreadsheet",
  doc: "document",
  docx: "document",
  dotx: "document",
  odt: "document",
  rtf: "document",
  pages: "document",
  ppt: "presentation",
  pptx: "presentation",
  odp: "presentation",
  key: "presentation",
  txt: "text",
  log: "text",
  md: "markdown",
  markdown: "markdown",
  mdx: "markdown",
  json: "code",
  yaml: "code",
  yml: "code",
  toml: "code",
  xml: "code",
  js: "code",
  ts: "code",
  tsx: "code",
  jsx: "code",
  py: "code",
  rb: "code",
  go: "code",
  rs: "code",
  java: "code",
  sh: "code",
  sql: "sql",
  html: "html",
  htm: "html",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  heic: "image",
  heif: "image",
  bmp: "image",
  tif: "image",
  tiff: "image",
  svg: "vector",
  mp4: "video",
  mov: "video",
  webm: "video",
  mkv: "video",
  mp3: "audio",
  wav: "audio",
  m4a: "audio",
  aac: "audio",
  zip: "archive",
  gz: "archive",
  tar: "archive",
  "7z": "archive",
  rar: "archive",
};

export const MIME_KINDS: Record<string, FileTypeKind> = {
  "application/pdf": "pdf",
  "text/csv": "csv",
  "text/tab-separated-values": "csv",
  "application/vnd.ms-excel": "spreadsheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "spreadsheet",
  "application/vnd.oasis.opendocument.spreadsheet": "spreadsheet",
  "application/msword": "document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "document",
  "application/vnd.oasis.opendocument.text": "document",
  "application/rtf": "document",
  "application/vnd.ms-powerpoint": "presentation",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "presentation",
  "text/plain": "text",
  "text/markdown": "markdown",
  "application/json": "code",
  "application/x-yaml": "code",
  "application/xml": "code",
  "application/sql": "sql",
  "text/html": "html",
  "image/svg+xml": "vector",
  "application/zip": "archive",
  "application/gzip": "archive",
  "application/x-tar": "archive",
  "application/x-7z-compressed": "archive",
  "application/vnd.google-apps.spreadsheet": "google-sheets",
  "application/vnd.google-apps.document": "google-docs",
  "application/vnd.google-apps.presentation": "google-slides",
};

/*
 * Exact MIME first (it is authoritative when a source sets it), then the
 * extension (uploads often arrive as octet-stream), then the MIME family.
 */
export function fileTypeKind(file: {
  fileName: string;
  contentType?: string | null;
}): FileTypeKind {
  const mime = (file.contentType ?? "").trim().toLowerCase();
  const exact = MIME_KINDS[mime];
  if (exact) return exact;
  const dot = file.fileName.lastIndexOf(".");
  const extension =
    dot > 0
      ? file.fileName
          .slice(dot + 1)
          .trim()
          .toLowerCase()
      : "";
  const byExtension = EXTENSION_KINDS[extension];
  if (byExtension) return byExtension;
  if (mime.startsWith("application/vnd.google-apps.")) return "google-drive";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("text/")) return "text";
  return "generic";
}

export function fileTypeLabel(kind: FileTypeKind): string {
  return FILE_TYPES[kind].label;
}

const RESOURCE_MARKS: Record<
  ResourceMarkKey,
  { colour: string; glyph: FileTypeGlyphKey }
> = {
  // A PDF mark draws its three letters; its glyph stands in on a chip's disc,
  // where letters would be too small to read.
  pdf: { colour: "#D9483B", glyph: "markDocument" },
  document: { colour: "#4A84EA", glyph: "markDocument" },
  sheet: { colour: "#23975A", glyph: "markSheet" },
  slides: { colour: "#EA7A43", glyph: "markSlides" },
  image: { colour: "#8A63E8", glyph: "markImage" },
  video: { colour: "#8A63E8", glyph: "markVideo" },
  audio: { colour: "#8A63E8", glyph: "markAudio" },
  archive: { colour: "#C58B22", glyph: "markArchive" },
  code: { colour: "#6B7686", glyph: "markCode" },
  data: { colour: "#6B7686", glyph: "markData" },
  link: { colour: "#6B7686", glyph: "markLink" },
  mail: { colour: "#6B7686", glyph: "markMail" },
  file: { colour: "#6B7686", glyph: "markFile" },
};

// Word, Excel and PowerPoint files show the product's mark; the open formats
// that share their kind (Pages, Numbers, Keynote, ODF) keep the drawn mark.
export const OFFICE_PRODUCTS: Record<string, string> = {
  doc: "word",
  docx: "word",
  dotx: "word",
  xls: "excel",
  xlsx: "excel",
  xlsm: "excel",
  xltx: "excel",
  ppt: "powerpoint",
  pptx: "powerpoint",
  "application/msword": "word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "word",
  "application/vnd.ms-excel": "excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "excel",
  "application/vnd.ms-powerpoint": "powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "powerpoint",
};

export type ResourceMarkSource =
  | { brand: BrandIcon }
  | { mark: ResourceMarkKey };

/** The mark a kind of file shows when only its kind is known. */
export function fileTypeMark(kind: FileTypeKind): ResourceMarkSource {
  const { mark, product } = FILE_TYPES[kind];
  const brand = product ? sourceIcon(product) : null;
  return brand ? { brand } : { mark };
}

/** The mark a file shows: its product's mark, else its format's. */
export function fileResourceMark(file: {
  fileName: string;
  contentType?: string | null;
}): ResourceMarkSource {
  const kind = fileTypeKind(file);
  if (kind === "document" || kind === "spreadsheet" || kind === "presentation") {
    const mime = (file.contentType ?? "").split(";")[0]!.trim().toLowerCase();
    const dot = file.fileName.lastIndexOf(".");
    const extension =
      dot > 0 ? file.fileName.slice(dot + 1).trim().toLowerCase() : "";
    const product = OFFICE_PRODUCTS[mime] ?? OFFICE_PRODUCTS[extension];
    const brand = product ? sourceIcon(product) : null;
    if (brand) return { brand };
  }
  return fileTypeMark(kind);
}

/** The colour a chip fills its round disc with for a mark. */
export function resourceMarkColour(mark: ResourceMarkKey): string {
  return RESOURCE_MARKS[mark].colour;
}

/** A mark's white glyph alone, for a chip's filled disc. */
export function ResourceMarkGlyph({
  mark,
  className,
}: {
  mark: ResourceMarkKey;
  className?: string;
}) {
  const glyph = FILE_TYPE_GLYPHS[RESOURCE_MARKS[mark].glyph];
  return (
    <svg
      viewBox={glyph.viewBox}
      fill="currentColor"
      aria-hidden
      className={cn("size-[62%] text-white", className)}
    >
      {glyph.paths.map((path) => (
        <path key={path.slice(0, 24)} d={path} />
      ))}
    </svg>
  );
}

/** A format's mark, a white glyph (PDF's letters) on its colour, at any size. */
export function ResourceMark({
  mark,
  className,
  title,
}: {
  mark: ResourceMarkKey;
  className?: string;
  title?: string;
}) {
  const { colour, glyph } = RESOURCE_MARKS[mark];
  const data = FILE_TYPE_GLYPHS[glyph];
  return (
    <svg
      viewBox="0 0 24 24"
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      data-resource-mark={mark}
      className={cn("size-6 shrink-0", className)}
    >
      {title ? <title>{title}</title> : null}
      <rect width="24" height="24" rx="5.5" fill={colour} />
      {mark === "pdf" ? (
        <text
          x="12"
          y="14.7"
          textAnchor="middle"
          fill="#fff"
          fontSize="7.4"
          fontWeight="700"
          letterSpacing="-0.15"
        >
          PDF
        </text>
      ) : (
        <svg x="5" y="5" width="14" height="14" viewBox={data.viewBox} fill="#fff">
          {data.paths.map((path) => (
            <path key={path.slice(0, 24)} d={path} />
          ))}
        </svg>
      )}
    </svg>
  );
}

/** A mark from its source: the product's own image or the drawn format mark. */
export function ResourceMarkView({
  source,
  className,
  title,
}: {
  source: ResourceMarkSource;
  className?: string;
  title?: string;
}) {
  if ("mark" in source)
    return <ResourceMark mark={source.mark} className={className} title={title} />;
  return (
    // Bundled product marks only, never a provider-supplied image URL.
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={source.brand.src}
      alt={title ?? ""}
      title={title}
      className={cn(
        "size-6 shrink-0 object-contain",
        source.brand.monochrome && "dark:invert",
        className,
      )}
    />
  );
}

/** The mark for one file-type kind; sized by className. */
export function FileTypeGlyph({
  kind,
  className,
  title,
}: {
  kind: FileTypeKind;
  className?: string;
  title?: string;
}) {
  return (
    <ResourceMarkView
      source={fileTypeMark(kind)}
      title={title ?? FILE_TYPES[kind].label}
      className={cn("size-4", className)}
    />
  );
}

/** The mark for a concrete file, classified by MIME type and extension. */
export function FileGlyph({
  file,
  className,
}: {
  file: { fileName: string; contentType?: string | null };
  className?: string;
}) {
  return (
    <ResourceMarkView
      source={fileResourceMark(file)}
      title={FILE_TYPES[fileTypeKind(file)].label}
      className={cn("size-4", className)}
    />
  );
}

/** Folder glyph in the macOS-style blue the rest of the set assumes. */
export function FolderGlyph({
  open = false,
  className,
  title = "Folder",
}: {
  open?: boolean;
  className?: string;
  title?: string;
}) {
  const glyph = FILE_TYPE_GLYPHS[open ? "folderOpen" : "folder"];
  return (
    <svg
      viewBox={glyph.viewBox}
      fill="currentColor"
      role="img"
      aria-label={title}
      className={cn(
        "size-4 shrink-0 text-badge-sky-foreground",
        className,
      )}
    >
      <title>{title}</title>
      {glyph.paths.map((path) => (
        <path key={path.slice(0, 24)} d={path} />
      ))}
    </svg>
  );
}
